<template>
  <div>
    <LoadingState
      v-if="loading"
      :loading="loading"
    />
    <v-container
      v-else
      grid-list-xl
      fluid
    >
      <v-layout
        row
        wrap
        class="mt-3"
      >
        <!-- Toolbar Navigation -->
        <v-toolbar
          flat
          color="transparent"
          class="mb-4 mt-4"
          height="20px"
        >
          <v-btn
            icon
            class="text--secondary"
            @click="goBack"
          >
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <span class="text-capitalize subheading text--secondary">
            {{ $t('Settings') }}
          </span>
        </v-toolbar>

        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Notifications') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('List of notifications types that should be enabled.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-combobox
                    v-model="settings.channels.enabled"
                    :items="['mail', 'nexmo']"
                    :label="$t('Enabled Channels')"
                    :return-object="false"
                    multiple
                    small-chips
                    deletable-chips
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex
          xs12
        >
          <v-divider/>

          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isMailEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Emails "from"') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('Configure from what address system emails should be send.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-text-field
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.email_from"
                    :error-messages="emailMessage"
                    :label="$t('From Email')"
                    outline
                  />
                  <v-text-field
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.name_from"
                    :label="$t('From Name')"
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isMailEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Emails BCC') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('Configure BCC email and list of templates that should use it.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-text-field
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.bcc_email"
                    :label="$t('BCC Email')"
                    outline
                  />
                  <v-combobox
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.bcc_notifications"
                    :items="templates"
                    :label="$t('Templates list')"
                    :return-object="false"
                    multiple
                    small-chips
                    deletable-chips
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isMailEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Disabling email notifications') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('List of disabled emails notifications or emails attachments.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-combobox
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.disabled"
                    :items="templates"
                    :label="$t('Disabled Templates')"
                    :return-object="false"
                    multiple
                    small-chips
                    deletable-chips
                    outline
                  />

                  <v-combobox
                    :disabled="!isMailEnabled"
                    v-model="settings.mailing.disabled_attachment"
                    :items="attachmentTemplates"
                    :label="$t('Disabled Attachments')"
                    :return-object="false"
                    multiple
                    small-chips
                    deletable-chips
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex
          xs12
        >
          <v-divider/>

          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isNexmoEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Nexmo SMS Credentials') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('Nexmo SMS gateway configuration required for properly working SMS notifications.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-text-field
                    :disabled="!isNexmoEnabled"
                    v-model="settings.sms.nexmo_key"
                    :label="$t('Key')"
                    outline
                  />
                  <v-text-field
                    :disabled="!isNexmoEnabled"
                    v-model="settings.sms.nexmo_secret"
                    :label="$t('Secret')"
                    type="password"
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isNexmoEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('SMS "from"') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('Configure from what name/number system SMS messages should be send.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-text-field
                    :disabled="!isNexmoEnabled"
                    v-model="settings.sms.from"
                    :label="$t('From Name/Number')"
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          xs12
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              :class="!isNexmoEnabled ? 'text--disabled' : ''"
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Disabling SMS notifications') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('List of disabled SMS notifications.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <template v-if="settings">
                  <v-combobox
                    :disabled="!isNexmoEnabled"
                    v-model="settings.sms.disabled"
                    :items="templates"
                    :label="$t('Disabled Templates')"
                    :return-object="false"
                    multiple
                    small-chips
                    deletable-chips
                    outline
                  />
                </template>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

    </v-container>
    <v-layout
      v-if="!loading"
      row
      wrap
      color="transparent"
    >
      <v-flex
        xs12
        ma-3
      >
        <v-divider mb-3/>
        <v-btn
          large
          class="primary right mt-3"
          @click="save()"
        >
          Save
        </v-btn>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import LoadingState from '@/components/LoadingState'

export default {
  name: 'SettingsNotifications',
  components: {
    LoadingState
  },

  data () {
    return {
      loading: false,
      settings: null,
      groups: [],
      templates: [],
      attachmentTemplates: [
        {
          text: 'order confirmation',
          value: 'order.confirmation'
        },
        {
          text: 'payment confirmation',
          value: 'payment.confirmation'
        },
        {
          text: 'order refund',
          value: 'order.refund'
        }
      ]
    }
  },

  computed: {
    emailMessage () {
      return this.settings.mailing.name_from && !this.settings.mailing.email_from ? this.$t('Email is required when Name is given.') : ''
    },

    isMailEnabled () {
      return this.isEnabled('mail')
    },

    isNexmoEnabled () {
      return this.isEnabled('nexmo')
    }
  },

  async mounted () {
    this.loading = true
    this.settings = await this.$http.make('settings').find('notifications')
    const templates = await this.$http.make('notification').limit(-1).get()
    this.templates = templates.map(template => {
      return {
        text: template.code.replaceAll('.', ' '),
        value: template.code
      }
    })

    this.loading = false
  },

  methods: {
    isEnabled (channel) {
      if (!this.settings || !this.settings.channels || !this.settings.channels.enabled.length) {
        return false
      }
      return this.settings.channels.enabled.includes(channel)
    },

    goBack () {
      this.$router.push({ name: 'settings' })
    },

    async save () {
      try {
        const channels = this.$http.make('settings', { data: this.settings.channels })
        channels.code_url = 'notifications/channels'
        channels.save()

        if (this.isEnabled('mail')) {
          const mailing = this.$http.make('settings', { data: this.settings.mailing })
          mailing.code_url = 'notifications/mailing'
          mailing.save()
        }

        if (this.isEnabled('nexmo')) {
          const sms = this.$http.make('settings', { data: this.settings.sms })
          sms.code_url = 'notifications/sms'
          sms.save()
        }

        this.$toast(this.$t('Settings saved'))
      } catch (e) {
        this.$toast.error(this.$t(e.message))
      }
    }
  }
}
</script>
